import { ReactElement, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Button from '../controls/Button';
import SidePanelContainer from '../controls/SidePanelContainer';
import { ADMIN_ROLE, RHFValidations, ROLES, TOAST_MESSAGES } from '../constants/global-constants';
import { isAdminRole } from '../services/session-storage-service';
import { createAddUSer, editUser } from '../services/dashboard-service';
import { useData } from '../context/data.context';
import CustomInputBox from '../controls/CustomInputBox';
import CustomDropdown from '../controls/CustomDropdown';
import { ActionData, UserDetails } from '../constants/telosair-interfaces';

export type User = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  inviteLink: string;
};

type AddUserModalProps = {
  handleClose: () => void;
  actionData?: ActionData<UserDetails>;
};

/**
 * Modal - AddUserModal.
 * @param props - Properties of AddUserModal.
 * @returns ReactElement.
 */
export default function AddUserModal(props: AddUserModalProps): ReactElement {
  const { handleClose, actionData } = props;
  const { clientData } = useData();
  const [loading, setloading] = useState(false);
  const methods = useForm<User>({
    mode: 'all'
  });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (actionData?.type === 'edit') {
      reset({
        firstName: actionData.data.firstName,
        lastName: actionData.data.lastName,
        email: actionData.data.userName,
        // Older admin spellings (Owner, Administrator) all mean Admin now.
        role: isAdminRole(actionData.data.role) ? ADMIN_ROLE : actionData.data.role
      });
    }
  }, [actionData]);

  /**
   * Function used to create Add User.
   * @param formData - Formdata.
   */
  const handleAddUSer = async (formData: User): Promise<void> => {
    if (clientData?.id) {
      const data = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        status: 'active',
        userName: formData.email,
        clientId: clientData.id,
        id: actionData?.type === 'edit' ? actionData.data.id : 0
      };
      try {
        setloading(true);
        if (actionData?.type === 'edit') {
          await editUser(data, actionData.data.id);
          toast.success(TOAST_MESSAGES.created);
        } else {
          await createAddUSer(data);
          toast.success(TOAST_MESSAGES.created);
        }
        setloading(false);
        handleClose();
      } catch (e: any) {
        setloading(false);
        toast.error(e?.response?.data?.message || TOAST_MESSAGES.error);
      }
    }
  };

  return (
    <SidePanelContainer closeModal={() => handleClose()}>
      <div className="pt-3 flex text-gray-500 dark:text-gray-300 text-lg">
        {actionData?.type === 'edit' ? 'Edit user' : 'Add user'}
      </div>
      <div className="pb-3 flex text-gray-500 dark:text-gray-300 text-sm">
        {actionData?.type === 'edit'
          ? 'Edit user and customise permissions'
          : 'Create user and customise permissions'}
      </div>
      {/* eslint-disable-next-line */}
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(handleAddUSer)}>
          <CustomInputBox
            validationObject={{ required: 'Please enter the first name' }}
            label="First Name"
            idname="firstName"
            autoComplete="given-name"
          />
          <CustomInputBox
            validationObject={{ required: 'Please enter the last name' }}
            label="Last Name"
            idname="lastName"
            autoComplete="family-name"
          />
          <CustomDropdown
            validationObject={{ required: 'Please select a role' }}
            id="role"
            label="Role"
            options={ROLES}
          />
          <CustomInputBox
            validationObject={RHFValidations.emailRequired}
            label="Email"
            idname="email"
            autoComplete="email"
            isDisabled={actionData?.type === 'edit'}
          />
          {/* <InputBox
        label="Phone No."
        textValue={user.phone}
        setText={(text) => handleInput(text, 'phone')}
      />
      <InputBox
        label="Invite Link"
        textValue={user.inviteLink}
        setText={() => null}
        actionElement={
          <button
            className="absolute right-2 bg-gray-800 flex items-center text-primary-200 top-2 bottom-2 text-xs px-2 rounded"
            type="button"
            onClick={() => navigator.clipboard.writeText(user.inviteLink)}
          >
            <div className="material-icons-outlined text-xs">copy</div>
            <div>Copy Link</div>
          </button>
        }
      /> */}
          <div
            style={{ width: 'inherit' }}
            className="p-5 fixed bottom-0 right-0 flex justify-center gap-3 bg-white dark:bg-container-600"
          >
            <div>
              <Button
                theme="secondary-color"
                size="sm"
                name="Discard"
                onClick={() => handleClose()}
                isBlockButton
              />
            </div>
            <Button
              theme="primary"
              size="sm"
              name="Save Changes"
              buttonType="submit"
              loading={loading}
              isBlockButton
            />
          </div>
        </form>
      </FormProvider>
    </SidePanelContainer>
  );
}
